/** Types partagés entre l'API et le front. Aucune dépendance runtime. */

import type {
  EtapeCollecte,
  FamilleArtisanat,
  ModeOrdonnanceur,
  NiveauNotification,
  StatutCollecte,
  StatutConfigCollecte,
  UserRole,
} from '../enums/index.js';

// ── Authentification ───────────────────────────────────────────────────────

/**
 * Charge utile du JWT d'accès.
 *
 * Volontairement minimale : la source de vérité reste la base, relue à chaque
 * requête par la stratégie JWT. Aucune notion de tenant — l'application est
 * mono-organisation.
 */
export interface JwtPayload {
  /** Identifiant du compte. */
  sub: string;
  roles: UserRole[];
  email?: string;
  nom?: string;
  prenom?: string;
  ssoProviderId?: string;
  /**
   * Présent uniquement sur les tickets à usage unique (MFA, échange SSO).
   * Un jeton portant ce claim est rejeté par la stratégie d'accès.
   */
  typ?: 'mfa' | 'sso';
  iat?: number;
  exp?: number;
}

/** Utilisateur authentifié, attaché à la requête par la stratégie JWT. */
export interface AuthUser {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  roles: UserRole[];
  /** Familles assignées. Vide pour un rôle transverse : aucun cloisonnement. */
  familles: FamilleArtisanat[];
  mustChangePassword: boolean;
  mfaEnabled: boolean;
  actif: boolean;
}

/** Réponse d'une authentification réussie. */
export interface LoginResponse {
  user: AuthUser;
  /** Espace d'accueil déduit du rôle principal. */
  home: string;
}

/** Réponse d'une authentification en attente du second facteur. */
export interface MfaChallenge {
  mfaRequired: true;
  /** Ticket à usage unique, à renvoyer avec le code TOTP. */
  ticket: string;
}

/** Discrimine les deux issues possibles d'un `POST /auth/login`. */
export function isMfaChallenge(value: LoginResponse | MfaChallenge): value is MfaChallenge {
  return (value as MfaChallenge).mfaRequired === true;
}

// ── Pagination ─────────────────────────────────────────────────────────────

export interface PaginationQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

// ── Erreurs ────────────────────────────────────────────────────────────────

/**
 * Forme unique de toute réponse d'erreur de l'API.
 * Aucun message interne ni pile d'appel n'y transite.
 */
export interface ApiError {
  statusCode: number;
  message: string;
  error: string;
  /** Code applicatif stable, exploitable par le front (ex. `password_change_required`). */
  code?: string;
  path: string;
  timestamp: string;
}

/** Codes applicatifs que le front doit savoir intercepter. */
export const API_ERROR_CODE = {
  PASSWORD_CHANGE_REQUIRED: 'password_change_required',
  MFA_REQUIRED: 'mfa_required',
  ACCOUNT_LOCKED: 'account_locked',
  QUOTA_EPUISE: 'quota_epuise',
  HORS_PERIMETRE: 'hors_perimetre',
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODE)[keyof typeof API_ERROR_CODE];

// ── Notifications ──────────────────────────────────────────────────────────

export interface NotificationDto {
  id: string;
  titre: string;
  message: string;
  niveau: NiveauNotification;
  lien?: string;
  lue: boolean;
  createdAt: string;
}

// ── Quota de collecte ──────────────────────────────────────────────────────

/**
 * État du quota d'appels. La fenêtre est glissante sur 24 h : chaque appel se
 * libère exactement 24 h après avoir été passé, sans remise à zéro horaire.
 */
export interface EtatQuota {
  limiteJour: number;
  /**
   * Appels disponibles, recalés sur le dernier décompte annoncé par la
   * source : le journal ignore les appels d'une étape coupée net, pas elle.
   */
  restant: number;
  /** Appels journalisés sur la fenêtre. */
  consommes24h: number;
  /** Dernier décompte annoncé par la source sur la fenêtre, s'il y en a un. */
  annonceSource: { restant: number; quand: string } | null;
  /** Retour de capacité à venir, du plus proche au plus lointain. */
  liberations: { quand: string; appels: number; cumul: number }[];
}

// ── Planification des collectes ────────────────────────────────────────────

/** Budget que les lancements automatiques peuvent consommer sur la fenêtre de 24 h. */
export interface BudgetAutomatique {
  limiteJour: number;
  /** Part du quota réservée à l'automatique (%). */
  partQuota: number;
  /** `limiteJour × partQuota`, arrondi à l'entier inférieur. */
  plafondAuto: number;
  consommesAuto24h: number;
  /**
   * Appels encore disponibles toutes origines confondues : c'est le budget
   * d'un tour lancé à la main, que la part automatique ne borne pas.
   */
  restantTotal: number;
  /** Ce qu'un passage périodique peut dépenser maintenant. */
  disponibleAuto: number;
}

export interface RequetePlanifiee {
  id: string;
  nom: string;
  tagSource: string | null;
  famille: FamilleArtisanat;
  statut: StatutConfigCollecte;
  nbArticlesSource: number | null;
  derniereCollecteLe: string | null;
  /**
   * La dernière collecte de la requête a été interrompue : la prochaine
   * reprend là où elle s'est arrêtée, et la requête passe en tête de file.
   */
  aReprendre?: boolean;
}

/** Poids d'une famille en mode couverture. */
export interface PoidsFamillePlanification {
  famille: FamilleArtisanat;
  /** Articles de la famille au catalogue. */
  articles: number;
  /** Part de la famille dans le catalogue (0 à 1). */
  part: number;
  /** Part de la collecte qui lui revient (0 à 1). */
  poids: number;
  /** Articles entrés au catalogue sur 7 jours : ce que la couverture aligne sur les poids. */
  nouveaux7j: number;
  /** Articles nouveaux qu'un lancement lui rapporte d'ordinaire (moyenne sur 7 jours). */
  rendement: number;
  /** Requêtes éligibles dans la famille. */
  candidates: number;
}

// ── Collecte en direct ─────────────────────────────────────────────────────

export type NiveauEvenement = 'info' | 'succes' | 'avertissement' | 'erreur';

export interface EvenementCollecte {
  ts: string;
  niveau: NiveauEvenement;
  message: string;
}

/** Compteurs cumulés d'une collecte, tenus par le pipeline au fil des étapes. */
export interface CompteursCollecte {
  appels: number;
  /** Dernier « restant » annoncé par la source dans ses en-têtes. */
  restantSource: number | null;
  /** Articles lus à la source, déjà connus compris. */
  articles: number;
  /** Articles qui n'étaient pas encore en base. */
  articlesNouveaux: number;
  boutiques: number;
  photos: number;
  /** Avis lus à la source, déjà connus compris : chaque boutique relit au moins sa première page. */
  avis: number;
  /** Avis qui n'étaient pas encore en base : les ventes nouvellement mesurées. */
  avisNouveaux: number;
  variantes: number;
}

export interface EtapeCollecteEnCours {
  etape: EtapeCollecte;
  motCle: string | null;
  debut: string;
  progression: { fait: number; total: number; unite: string } | null;
}

export interface EtapeCollecteTerminee {
  etape: EtapeCollecte;
  motCle: string | null;
  statut: StatutCollecte;
  appels: number;
  lignes: number;
  dureeMs: number;
}

export interface CollecteEnCours {
  configId: string | null;
  nom: string;
  famille: FamilleArtisanat;
  automatique: boolean;
  debut: string;
  etape: EtapeCollecteEnCours | null;
  compteurs: CompteursCollecte;
  etapesTerminees: EtapeCollecteTerminee[];
}

export interface DerniereCollecte {
  nom: string;
  famille: FamilleArtisanat;
  automatique: boolean;
  debut: string;
  fin: string;
  compteurs: CompteursCollecte;
  interrompu: string | null;
  erreur: string | null;
}

/** Ce que l'onglet En direct affiche. Tenu en mémoire par l'API. */
export interface CollecteEnDirect {
  enCours: CollecteEnCours | null;
  derniere: DerniereCollecte | null;
  /** Derniers événements, du plus récent au plus ancien. */
  journal: EvenementCollecte[];
}

/**
 * Ce qui fixe l'heure du prochain lancement automatique :
 * - `passage` : tout est prêt, la collecte part au prochain passage ;
 * - `budget` : la part automatique ou le quota doit d'abord se reconstituer ;
 * - `eligibilite` : aucune requête n'est à rafraîchir avant l'échéance ;
 * - `tour` : un tour est en cours, sa requête suivante part aussitôt ;
 * - `sans-requete` : le mode n'a aucune requête à lancer ;
 * - `part-insuffisante` : la part automatique ne suffit à aucun lancement.
 */
export type MotifProchaineCollecte =
  'passage' | 'budget' | 'eligibilite' | 'tour' | 'sans-requete' | 'part-insuffisante';

/**
 * Prévision du prochain lancement automatique, estimée à l'instant de la
 * lecture : un lancement à la main ou un réglage modifié la déplace.
 */
export interface ProchaineCollecte {
  motif: MotifProchaineCollecte;
  /** Passage de l'ordonnanceur qui devrait la lancer ; null quand aucun n'est prévisible. */
  quand: string | null;
  /** Requête en tête de file, ou la première à redevenir éligible. */
  requete: { nom: string; tagSource: string | null; famille: FamilleArtisanat } | null;
}

/** État de l'ordonnanceur, pour l'onglet Planification. */
export interface EtatPlanification {
  mode: ModeOrdonnanceur;
  enCours: boolean;
  budget: BudgetAutomatique;
  /** Requêtes éligibles au prochain tour, tous modes confondus. */
  eligibles: number;
  /** File des prochaines requêtes dans l'ordre où le mode les lancera. */
  prochaines: RequetePlanifiee[];
  /** Répartition par famille ; vide hors mode couverture. */
  familles: PoidsFamillePlanification[];
  derniereLancee: {
    nom: string;
    famille: FamilleArtisanat;
    quand: string;
    statut: string;
    appels: number;
  } | null;
  prochaineCollecte: ProchaineCollecte;
}
